import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import AppBar from "../../common/AppBar";
import AppTable from "../../common/AppTable/AppTable";
// import "./SearchQuery.css";
import { fetchRevsonSearch } from "../../redux/procedureRevisionSlice";
import Loader from "../../common/Loader";
import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import SelectRowButton from "../../common/Buttons/TableButtons/SelectRowButton";
import { useRedirectToRevision, getCurrentStatus } from "../../util/AppUtils.jsx";
import {
  fetchTrainingStatusLabelForProcedureRevisionForTrainee,
  getProcedureRevisionLookupKeyFromRow,
  isRevisionWorkflowActor,
  normalizeProcedureRevisionIdKey,
} from "../../util/trainingLogStatus";
import { getGlobalAppViewMode } from "../../util/appViewModeStorage";

const TRAINING_STATUS_FETCH_CHUNK = 20;

async function runTrainingFetchesInChunks(ids, chunkSize, fetchOne) {
  if (!ids.length) return [];
  const chunks = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    chunks.push(ids.slice(i, i + chunkSize));
  }
  const chunkResults = await Promise.all(
    chunks.map((chunk) => Promise.all(chunk.map((id) => fetchOne(id)))),
  );
  return chunkResults.flat();
}

const SearchQuery = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const redirectToRevision = useRedirectToRevision();
  const authUser = useSelector((state) => state.auth.user);
  /**
   * Live: Training status + Rev + Action (revision Status hidden — search is Released-only).
   * Edit: Item Code … Rev + Status + Action.
   */
  const isLiveMode = (() => {
    const nav = location.state?.isLiveMode;
    if (typeof nav === "boolean") return nav;
    return getGlobalAppViewMode() === true;
  })();

  // State for pagination
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [trainingStatusByProcedureRevisionId, setTrainingStatusByProcedureRevisionId] =
    useState({});
  const trainingStatusByProcedureRevisionIdRef = useRef({});
  trainingStatusByProcedureRevisionIdRef.current = trainingStatusByProcedureRevisionId;

  // Get data from Redux
  const { revisions, pagination, loading } = useSelector(
    (state) => state.procedureRevision
  );

  const fetchTrainingStatusForProcedureRevision = useCallback(
    (procedureRevisionId, partRef) =>
      fetchTrainingStatusLabelForProcedureRevisionForTrainee(
        dispatch,
        procedureRevisionId,
        authUser?.id,
        partRef,
      ),
    [dispatch, authUser?.id],
  );

  const columns = [
    {
      headName: "Item Code",
      field: "part_number",
      sort: true,
      filter: true,
      width: "150px",
      required: false,
    },
    {
      headName: "Type",
      field: "type_name",
      sort: true,
      width: "200px",
      filter: true,
    },
    {
      headName: "Description",
      field: "description",
      filter: true,
    },
    ...(isLiveMode
      ? [
          {
            headName: "Training status",
            field: "training_status",
            width: "160px",
            sort: false,
            filter: true,
          },
        ]
      : []),
    {
      headName: "Rev",
      field: "rev",
      width: "100px",
      filter: true,
    },
    ...(!isLiveMode
      ? [
          {
            headName: "Status",
            field: "status",
            width: "120px",
            filter: true,
          },
        ]
      : []),
    {
      headName: "Action",
      field: "action",
      width: "100px",
    },
  ];

  // Fetch data when searchQuery changes
  useEffect(() => {
    if (searchQuery.length >= 0) {
      const normalizedQuery = String(searchQuery || "").replace(/^\?/, "");
      const params = new URLSearchParams(normalizedQuery);
      if (isLiveMode) {
        // Live/view mode search must always be constrained to released revisions.
        params.set("status", "Released");
      }
      dispatch(fetchRevsonSearch({ searchQuery: params.toString() }));
    }
  }, [dispatch, searchQuery, isLiveMode]);

  /** Per revision + logged-in trainee: only needed for Live mode table (same as Add Module). */
  useEffect(() => {
    if (!isLiveMode) return;
    if (!revisions?.length) return;

    const ids = Array.from(
      new Set(
        revisions
          .map((r) =>
            normalizeProcedureRevisionIdKey(
              r?.procedure_revision_id ?? r?.procedure_revision_ref ?? "",
            ),
          )
          .filter(Boolean),
      ),
    );
    if (!ids.length) return;

    const revisionIdToPartRef = new Map();
    for (const row of revisions) {
      const rid = normalizeProcedureRevisionIdKey(
        row?.procedure_revision_id ?? row?.procedure_revision_ref ?? "",
      );
      const pid = row?.part_ref ?? row?.part_id ?? row?.partRef;
      if (rid && pid) revisionIdToPartRef.set(rid, pid);
    }

    let cancelled = false;

    const hydrate = async () => {
      const missingToFetch = ids.filter(
        (id) =>
          trainingStatusByProcedureRevisionIdRef.current[
            normalizeProcedureRevisionIdKey(id)
          ] === undefined,
      );
      if (!missingToFetch.length) return;

      setTrainingStatusByProcedureRevisionId((prev) => {
        const next = { ...prev };
        for (const id of missingToFetch) next[id] = "Not Trained";
        return next;
      });

      const results = await runTrainingFetchesInChunks(
        missingToFetch,
        TRAINING_STATUS_FETCH_CHUNK,
        (id) => {
          const key = normalizeProcedureRevisionIdKey(id);
          return fetchTrainingStatusForProcedureRevision(
            id,
            revisionIdToPartRef.get(key),
          );
        },
      );

      if (cancelled) return;
      setTrainingStatusByProcedureRevisionId((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (!r?.procedureRevisionId) continue;
          next[r.procedureRevisionId] = r.status;
        }
        return next;
      });
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [revisions, fetchTrainingStatusForProcedureRevision, isLiveMode]);

  const revisionStatusLabel = (row) => {
    const fromChanges = getCurrentStatus(row);
    if (fromChanges && String(fromChanges).trim() && fromChanges !== "-") {
      return String(fromChanges).trim();
    }
    const fallback =
      row?.status ?? row?.revision_status ?? row?.current_status ?? null;
    if (fallback != null && String(fallback).trim() !== "") {
      return String(fallback).trim();
    }
    return "N/A";
  };

  // Handle navigation when action button is clicked
  const handleActionClick = (row) => {
    const currentStatus = getCurrentStatus(row);
    const revData = {
      type_name: row.type || row.type_name || "",
      part_number: row.part_number || row.partNumber || "",
      procedureRevisionId: row.procedure_revision_id || row.procedureRevisionId || "",
      description: row.description || row.part_description || "",
      status: currentStatus,
      partId: row.part_ref,
      revision: row.rev || row.revision || "",
    };
    /** Live mode (Directory): open Document in view mode; avoid stale sessionStorage edit state. */
    if (isLiveMode) {
      revData.isViewMode = true;
    }
    redirectToRevision(revData);
  };

  return (
    <div>
      <AppBar
        title="Enter Search Query"
        showTablesDropdown={false}
        buttons={[]}
        showProfileButton={false}
      />

      <AppTable
        columnsData={columns}
        showSearchInput={true}
        searchInputPosition="left"
        paginate={true}
        pagination={{
          limit: [limit, setLimit],
          offset: [offset, setOffset],
          pagination,
          maxVsbleButtons: 7,
          justifyContent: "flex-end",
          paginationPosition: {
            position: "right",
            leftContent: null,
            rightContent: null,
          },
          paginationShadow: true,
        }}
        onFilterSortHandler={setSearchQuery}
        usingInPopup={false}
      >
        {revisions && revisions.length > 0 ? (
          revisions.map((row, index) => (
            <tr key={row.procedure_revision_id+"_"+index}>
              <td style={{padding: "10px"}}>
                {row.part_url ? (
                  <a
                    href={row.part_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "black", textDecoration: "underline" }}
                  >
                    {row.part_number}
                  </a>
                ) : (
                  row.part_number
                )}
              </td>
              <td>{row.type || row.type_name || "N/A"}</td>
              <td><RenderHtml content={row.description || row.part_description}/></td>
              {isLiveMode && (
                <td>
                  {isRevisionWorkflowActor(
                    row.revisionRequiredBy ?? row.required_by,
                    authUser?.id,
                  )
                    ? "Trained"
                    : trainingStatusByProcedureRevisionId[
                        getProcedureRevisionLookupKeyFromRow(row)
                      ] ?? "Not Trained"}
                </td>
              )}
              <td>{row.rev || "N/A"}</td>
              {!isLiveMode && (
                <td>{revisionStatusLabel(row)}</td>
              )}
              <td>
                {
                  !row.is_maintained_outside_system && <SelectRowButton
                  onClick={() => handleActionClick(row)}
                  ariaLabel="View revision details"
                />
                }
                
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={columns.length} style={{ textAlign: "center" }}>
              {loading ? "Loading..." : "No data available"}
            </td>
          </tr>
        )}
      </AppTable>

      {loading && <Loader />}
    </div>
  );
};

export default SearchQuery;
